import type { ChatFileAttachment, EnvironmentApi, EnvironmentId } from "@cmmd-forge/contracts";

import { readEnvironmentApi } from "../environmentApi";
import {
  ensureEnvironmentAttachmentUploadTarget,
  resolveEnvironmentAttachmentUploadUrl,
} from "../environments/runtime/catalog";
import { readEnvironmentBearerToken } from "../environments/runtime/cloudRuntimeCredentialAccess";

export interface UploadEnvironmentAttachmentInput {
  readonly environmentId: EnvironmentId;
  readonly file: File;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
  readonly resolveApi?: (environmentId: EnvironmentId) => EnvironmentApi | undefined;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (!signal?.aborted) return;
  throw signal.reason ?? new DOMException("Aborted", "AbortError");
}

const RUNTIME_ATTACHMENT_UPDATE_MESSAGE =
  "This environment needs a Forge update before it can accept file attachments. Your draft was kept.";

function includesUnknownAttachmentUploadRequest(
  value: unknown,
  visited = new Set<object>(),
): boolean {
  if (typeof value === "string")
    return value.includes("Unknown request tag: attachments.createUploadUrl");
  if (typeof value !== "object" || value === null || visited.has(value)) return false;
  visited.add(value);
  if (value instanceof Error) {
    return (
      includesUnknownAttachmentUploadRequest(value.message, visited) ||
      includesUnknownAttachmentUploadRequest(value.cause, visited)
    );
  }
  return Object.values(value).some((child) =>
    includesUnknownAttachmentUploadRequest(child, visited),
  );
}

function normalizeUploadAuthorityError(cause: unknown): Error {
  if (includesUnknownAttachmentUploadRequest(cause)) {
    return new Error(RUNTIME_ATTACHMENT_UPDATE_MESSAGE, { cause });
  }
  if (cause instanceof Error && cause.message.trim().length > 0) return cause;
  if (typeof cause === "string" && cause.trim().length > 0) return new Error(cause, { cause });
  return new Error("Unable to request a file upload from this environment.", { cause });
}

/** Upload one user-selected file after an explicit caller action. */
export async function uploadEnvironmentAttachment(
  input: UploadEnvironmentAttachmentInput,
): Promise<ChatFileAttachment> {
  throwIfAborted(input.signal);
  ensureEnvironmentAttachmentUploadTarget(input.environmentId);
  const api = (input.resolveApi ?? readEnvironmentApi)(input.environmentId);
  if (!api) {
    throw new Error(`Environment API not found for environment ${input.environmentId}.`);
  }
  const mimeType = input.file.type || "application/octet-stream";
  const issued = await api.attachments
    .createUploadUrl({
      type: "file",
      name: input.file.name,
      mimeType,
      sizeBytes: input.file.size,
    })
    .catch((cause: unknown) => Promise.reject(normalizeUploadAuthorityError(cause)));
  throwIfAborted(input.signal);
  const bearerToken = await readEnvironmentBearerToken(input.environmentId);
  const uploadUrl = resolveEnvironmentAttachmentUploadUrl({
    environmentId: input.environmentId,
    relativeUrl: issued.relativeUrl,
  });
  const response = await (input.fetchImpl ?? fetch)(uploadUrl, {
    method: "POST",
    headers: {
      "Content-Type": mimeType,
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
    },
    body: input.file,
    ...(input.signal ? { signal: input.signal } : {}),
  });
  if (!response.ok) {
    throw new Error(`Attachment upload failed with status ${response.status}.`);
  }
  return {
    type: "file",
    id: issued.attachmentId,
    name: input.file.name,
    mimeType,
    sizeBytes: input.file.size,
  };
}
